/**
 * Types for the Everfall public API (/api/v1) and its key management.
 * Shared between core (routes + ApiServer module) and the panel (API Keys page).
 */
import type { DatabaseActionType } from '../core/modules/Database/databaseTypes';
import type { ApiScopeInfo } from './apiScopes';
import type { AdminLogCategory, AdminLogEntry } from './adminLogApiTypes';
import type { StaffActivityData } from './staffActivityApiTypes';
export type { AdminLogCategory, AdminLogEntry } from './adminLogApiTypes';
export type { StaffActivityCounts, StaffActivityData, StaffActivityMember } from './staffActivityApiTypes';

//Error codes returned inside the `error.code` field of the envelope
export const API_ERROR_CODES = [
    'UNAUTHORIZED',
    'FORBIDDEN',
    'NOT_FOUND',
    'VALIDATION_ERROR',
    'RATE_LIMITED',
    'CONFLICT',
    'SERVER_OFFLINE',
    'PLAYER_OFFLINE',
    'INTERNAL_ERROR',
] as const;
export type ApiErrorCode = typeof API_ERROR_CODES[number];

export type ApiErrorBody = {
    error: {
        code: ApiErrorCode;
        message: string;
        details?: unknown;
    };
};
export type ApiDataBody<T, M = undefined> = M extends undefined
    ? { data: T }
    : { data: T; meta: M };
export type ApiResp<T, M = undefined> = ApiDataBody<T, M> | ApiErrorBody;


/**
 * API key records
 */
export const API_KEY_PREFIX = 'txk';
export const API_KEY_NAME_MAX_LENGTH = 48;
export const API_KEY_ALLOWED_IPS_MAX = 32;

//As stored on disk (secret is hashed) and as returned to clients (no hash)
export type ApiKeyPublicRecord = {
    id: string;
    name: string;
    /** Scope ids (see apiScopes.ts). Empty = read-only key. */
    permissions: string[];
    createdBy: string;
    createdAt: number; //epoch ms
    lastUsedAt: number | null; //epoch ms
    expiresAt: number | null; //epoch ms
    allowedIps: string[]; //CIDR or plain IPs, empty = any
    revokedAt: number | null; //epoch ms
    revokedBy: string | null;
};

export type ApiKeyCreateReq = {
    name: string;
    /** Scope ids to grant. Empty = read-only key. */
    permissions: string[];
    expiresAt?: number | null;
    allowedIps?: string[];
};

export type ApiKeyCreateResp = ApiResp<{
    key: ApiKeyPublicRecord;
    /** Plaintext token, shown exactly once. */
    token: string;
}>;

export type ApiKeyListResp = ApiResp<{
    keys: ApiKeyPublicRecord[];
    /** The scopes a key can be given, for the panel selector. */
    scopes: ApiScopeInfo[];
}>;

export type ApiKeyUpdateScopesReq = {
    id: string;
    /** Scope ids that replace the key's current ones. Empty = read-only key. */
    permissions: string[];
};

export type ApiKeyUpdateScopesResp = ApiResp<{ key: ApiKeyPublicRecord }>;

export type ApiKeyRevokeResp = ApiResp<{ key: ApiKeyPublicRecord }>;

export type ApiMeResp = ApiResp<{
    key: ApiKeyPublicRecord;
    txAdminVersion: string;
    serverTime: number;
}>;


/**
 * Pagination (cursor based, used by list endpoints)
 */
export const API_PAGE_DEFAULT_LIMIT = 50;
export const API_PAGE_MAX_LIMIT = 200;
export type ApiPageMeta = {
    limit: number;
    /** Pass as `?cursor=` to get the next page, null when the list ended. */
    nextCursor: string | null;
};


/**
 * GET /status
 */
export type ApiStatusResp = ApiResp<{
    txAdmin: {
        version: string;
        configState: string;
        serverTime: number; //epoch ms
    };
    server: {
        name: string;
        health: 'OFFLINE' | 'ONLINE' | 'PARTIAL';
        healthReason: string;
        uptime: number; //ms since the server booted, 0 when offline
        isProcessAlive: boolean;
        isRunnerIdle: boolean;
        whitelistMode: 'disabled' | 'adminOnly' | 'approvedLicense' | 'discordMember' | 'discordRoles';
        playerCount: number;
        playerSlots: number | null;
        projectName: string | null;
        projectDesc: string | null;
        gameName: 'fivem' | 'redm' | null;
        cfxId: string | null;
        joinLink: string | null;
    };
    scheduler: {
        nextRestartAt: number | null; //epoch ms
        nextSkip: boolean;
        nextIsTemp: boolean;
    };
    discord: {
        status: 'disabled' | 'starting' | 'ready' | 'error';
    };
}>;


/**
 * Players
 */
export type ApiOnlinePlayer = {
    netid: number;
    displayName: string;
    pureName: string;
    license: string | null;
};
export type ApiOnlinePlayersResp = ApiResp<{ players: ApiOnlinePlayer[] }>;

export type ApiPlayerSummary = {
    license: string;
    displayName: string;
    pureName: string;
    playTime: number; //minutes
    joinedAt: number; //epoch ms
    lastConnectionAt: number; //epoch ms
    whitelistedAt: number | null; //epoch ms
    notes: string | null;
    /** Staff labels such as VIP, as typed. */
    tags: string[];
    /** Set while the player is on the watchlist. */
    watch: ApiPlayerWatch | null;
    isAdmin: boolean;
    isOnline: boolean;
};
export type ApiPlayerWatch = {
    reason: string;
    author: string;
    createdAt: number; //epoch ms
};
export type ApiPlayerSearchResp = ApiResp<{ players: ApiPlayerSummary[] }, ApiPageMeta>;

export type ApiPlayerNote = {
    id: string;
    text: string;
    author: string;
    createdAt: number; //epoch ms
};

export type ApiPlayerDetail = ApiPlayerSummary & {
    ids: string[];
    hwids: string[];
    /** Author and time of the newest note; `notes` holds its text. */
    notesLastEditedBy: string | null;
    notesLastEditedAt: number | null; //epoch ms
    /** Every staff note, newest first. */
    noteHistory: ApiPlayerNote[];
    /** Only when the player is online right now. */
    session: {
        netid: number;
        connectedAt: number; //epoch ms
        idsOnline: string[];
        hwidsOnline: string[];
    } | null;
    actions: ApiActionRecord[];
};
export type ApiPlayerDetailResp = ApiResp<{ player: ApiPlayerDetail }>;

export type ApiLinkedPlayer = {
    license: string;
    displayName: string;
    playTime: number; //minutes
    lastConnectionAt: number; //epoch ms
    isOnline: boolean;
    /** Identifiers (never IPs) this player shares with the requested one. */
    matchedIds: string[];
    matchedHwids: string[];
    /** Has a ban that is neither revoked nor expired. */
    isBanned: boolean;
    banCount: number; //not revoked
    warnCount: number; //not revoked
};
export type ApiPlayerLinkedResp = ApiResp<{
    players: ApiLinkedPlayer[];
    /** HWIDs shared by too many players to mean anything; they were not used for matching. */
    ignoredHwids: string[];
    /** More players matched than were returned. */
    truncated: boolean;
}>;

export type ApiPlayersStatsResp = ApiResp<{
    total: number;
    playedLast24h: number;
    joinedLast24h: number;
    joinedLast7d: number;
    onlineNow: number;
}>;


/**
 * Actions (bans, warns and kicks)
 */
export type ApiActionNote = {
    id: string;
    text: string;
    author: string;
    createdAt: number; //epoch ms
    editedAt: number | null; //epoch ms
};
export type ApiActionRecord = {
    id: string;
    /** Kicks and game sanctions are a record only: never active and never revoked. */
    type: 'ban' | 'warn' | 'kick' | 'sanction';
    playerName: string | null;
    ids: string[];
    hwids: string[];
    reason: string;
    author: string;
    createdAt: number; //epoch ms
    /** Bans only: null = permanent. Warns and kicks: always null. */
    expiresAt: number | null;
    /** Bans only: 'active' | 'expired' | 'permanent'; warns and kicks: null */
    banStatus: 'active' | 'expired' | 'permanent' | null;
    historical?: DatabaseActionType['historical'] | null;
    /** Warns only */
    acked: boolean | null;
    revokedAt: number | null; //epoch ms
    revokedBy: string | null;
    revokeReason: string | null;
    /** Staff notes, newest first. Never shown to the player. */
    notes: ApiActionNote[];
    /** Bans imported via POST /actions/import-ban: their idempotency key. Otherwise null. */
    externalRef: string | null;
    /** The punishment preset (ban template) a ban or warn was issued from, else null. */
    presetId: string | null;
    /** everfall: the rule link of that preset, else null */
    ruleUrl: string | null;
    /** Bans only: every change of the ban's length, oldest first. Empty otherwise. */
    lengthChanges: ApiBanLengthChange[];
    /** Bans only: where the ban's appeal stands, else null. Accepting does not lift the ban by itself. */
    appeal: ApiBanAppeal | null;
    /** Game sanctions only: what a game resource applied (jail, community service, mute). Else null. */
    sanction: { kind: string; length: string | null } | null;
};
export type ApiBanAppeal = {
    status: 'open' | 'denied' | 'accepted';
    ticket: string | null; //ticket link or number
    updatedBy: string; //who set this status
    updatedAt: number; //epoch ms
};
export type ApiBanLengthChange = {
    changedAt: number; //epoch ms
    changedBy: string;
    previousExpiresAt: number | null; //epoch ms, null = permanent
    expiresAt: number | null;
    reason: string;
};
export type ApiActionsSearchResp = ApiResp<{ actions: ApiActionRecord[] }, ApiPageMeta>;
export type ApiActionDetailResp = ApiResp<{ action: ApiActionRecord }>;
export type ApiActionsStatsResp = ApiResp<{
    totalWarns: number;
    warnsLast7d: number;
    totalBans: number;
    bansLast7d: number;
    totalKicks: number;
    kicksLast7d: number;
    byAdmin: { name: string; actions: number }[];
}>;
export type ApiActionNoteResp = ApiResp<{ note: ApiActionNote }>;


/**
 * Whitelist
 */
export type ApiWhitelistApproval = {
    identifier: string;
    playerName: string;
    playerAvatar: string | null;
    approvedAt: number; //epoch ms
    approvedBy: string;
};
export type ApiWhitelistRequest = {
    id: string;
    license: string;
    playerDisplayName: string;
    playerPureName: string;
    discordTag: string | null;
    discordAvatar: string | null;
    lastAttemptAt: number; //epoch ms
};
export type ApiWhitelistApprovalsResp = ApiResp<{ approvals: ApiWhitelistApproval[] }, ApiPageMeta>;
export type ApiWhitelistRequestsResp = ApiResp<{ requests: ApiWhitelistRequest[] }, ApiPageMeta>;


/**
 * Admins
 */
export type ApiAdminRecord = {
    name: string;
    master: boolean;
    permissions: string[];
    identifiers: string[]; //citizenfx / discord identifiers
};
export type ApiAdminsResp = ApiResp<{ admins: ApiAdminRecord[] }>;

/**
 * Audit log (GET /audit-log) and staff activity (GET /staff/activity), both need manage.admins
 */
export type ApiAuditLogQuery = {
    text?: string;
    admin?: string; //part of the author name
    categories?: AdminLogCategory[];
    from?: number; //epoch ms
    to?: number; //epoch ms, inclusive; pass `cursor` for the next older page
};
export type ApiAuditLogResp = ApiResp<{
    entries: AdminLogEntry[]; //oldest first
    truncated: boolean;
    cursor: number | null;
}>;
export type ApiStaffActivityResp = ApiResp<StaffActivityData>;


/**
 * Resources
 */
export type ApiResourceRecord = {
    name: string;
    status: string;
    path: string;
    version: string | null;
    author: string | null;
    description: string | null;
};
export type ApiResourcesResp = ApiResp<{ resources: ApiResourceRecord[] }>;


/**
 * Writes (phase 3)
 */
/** Ban duration: 'permanent' or '<n> hours|days|weeks|months', like the panel. */
export type ApiBanDuration = string;

/** `note` on a sanction is a staff note: saved on the action, never shown to the player. */
export type ApiBanPlayerReq = { reason: string; duration: ApiBanDuration; note?: string };
export type ApiWarnPlayerReq = { reason: string; note?: string };
export type ApiKickPlayerReq = { reason?: string; note?: string };
export type ApiKickPlayerResp = ApiResp<{
    ok: true;
    /** The kick record, null when the player had no identifiers to record. */
    action: ApiActionRecord | null;
}>;
export type ApiActionNoteReq = { text: string };
export type ApiRevokeActionReq = { reason?: string };
/** The new length counts from when the ban was issued. */
export type ApiChangeBanLengthReq = { duration: string; reason: string };
/** null clears the appeal. Accepting does not lift the ban by itself. */
export type ApiSetBanAppealReq = { status: ApiBanAppeal['status'] | null; ticket?: string | null };
export type ApiMessagePlayerReq = { message: string };
export type ApiSetWhitelistReq = { whitelisted: boolean };
export type ApiSetNoteReq = { note: string };
export type ApiPlayerNoteResp = ApiResp<{ note: ApiPlayerNote }>;
export type ApiWatchPlayerReq = { reason: string };
export type ApiWatchPlayerResp = ApiResp<{ watch: ApiPlayerWatch }>;
export type ApiSetTagsReq = { tags: string[] };
export type ApiSetTagsResp = ApiResp<{ tags: string[] }>;
export type ApiBanIdsReq = { identifiers: string[]; reason: string; duration: ApiBanDuration; note?: string };

export type ApiActionWriteResp = ApiResp<{
    action: ApiActionRecord;
    /** false when the action was saved but the in-game event could not be sent (server offline or stdin error). */
    eventSent: boolean;
}>;
export type ApiImportBanReq = {
    /** Idempotency key, e.g. `qbx-bans:1167`: 1-96 chars of A-Z a-z 0-9 _ . : - */
    externalRef: string;
    identifiers: string[];
    hwids?: string[];
    playerName?: string | null;
    reason: string;
    /** Label shown as the ban author; must not match a txAdmin admin name. */
    author: string;
    /** Epoch ms; omitted or null = permanent. */
    expiresAt?: number | null;
    /** Send the in-game `playerBanned` event (kick + webhooks). Default false. */
    notify?: boolean;
};
export type ApiImportBanResp = ApiResp<{
    action: ApiActionRecord;
    /** false when externalRef was already imported: the existing action is returned, nothing written. */
    created: boolean;
    /** Identifiers and hwids that failed validation and were not stored. */
    dropped: string[];
    eventSent: boolean;
}>;
export type ApiOkResp = ApiResp<{ ok: true }>;
export type ApiWhitelistApprovalResp = ApiResp<{ approval: ApiWhitelistApproval }>;
export type ApiWhitelistRequestsResolvedResp = ApiResp<{ removed: number }>;

export type ApiServerControlResp = ApiResp<{
    action: 'start' | 'stop' | 'restart';
    /** What happened: started, stopped, restarting, scheduled (restart delayed by the spawn backoff) or noop. */
    result: 'started' | 'stopped' | 'restarting' | 'scheduled' | 'noop';
    message: string;
}>;
export type ApiServerCommandReq = { command: string };
export type ApiAnnounceReq = { message: string };
export type ApiKickAllReq = { reason?: string };
export type ApiResourceCommandResp = ApiResp<{ resource: string | null; command: string }>;


/**
 * Phase 4: events and webhooks
 */
export const API_EVENT_TYPES = [
    'server.online',
    'server.partial',
    'server.offline',
    'server.shuttingDown',
    'server.scheduledRestart',
    'server.scheduledRestartSkipped',
    'server.nextRestartSkipped',
    'server.announcement',
    'server.configChanged',
    'player.joined',
    'player.left',
    'player.banned',
    'player.warned',
    'player.kicked',
    'player.sanctioned',
    'player.directMessage',
    'player.watchJoined',
    'player.possibleBanEvasion',
    'whitelist.player',
    'whitelist.preApproval',
    'whitelist.request',
    'action.revoked',
    'action.noteAdded',
    'action.lengthChanged',
    'action.appealChanged',
    'admin.login',
    'apiKey.firstUse',
    'webhook.test',
] as const;
export type ApiEventType = typeof API_EVENT_TYPES[number];

export const API_EVENTS_BUFFER_SIZE = 1000;
export const API_EVENTS_PAGE_MAX = 500;

export type ApiEvent = {
    id: string; //monotonic, usable as a cursor
    type: ApiEventType;
    ts: number; //epoch ms
    data: Record<string, unknown>;
};
export type ApiEventsMeta = {
    cursor: string | null; //id of the last event returned, pass as ?since=
    hasMore: boolean;
    dropped: boolean; //true when ?since= points before the buffer start (events were lost)
};
export type ApiEventsResp = ApiResp<{ events: ApiEvent[] }, ApiEventsMeta>;
export type ApiEventTypesResp = ApiResp<{ types: ApiEventType[] }>;

export const API_WEBHOOKS_MAX = 10;
export const API_WEBHOOK_NAME_MAX_LENGTH = 48;
export const API_WEBHOOK_URL_MAX_LENGTH = 512;
export const API_WEBHOOK_SECRET_MIN_LENGTH = 16;
export const API_WEBHOOK_SECRET_MAX_LENGTH = 128;
export const API_WEBHOOK_SIGNATURE_HEADER = 'x-txadmin-signature';
export const API_WEBHOOK_DELIVERIES_KEPT = 50;

export type ApiWebhookRecord = {
    id: string;
    name: string;
    url: string;
    events: ApiEventType[] | ['*'];
    enabled: boolean;
    createdBy: string;
    createdAt: number;
    deliveredCount: number;
    failedCount: number;
    lastDeliveryAt: number | null;
    lastDeliveryOk: boolean | null;
};
export type ApiWebhookCreateReq = {
    name: string;
    url: string;
    events: string[]; //event types, or ['*'] for everything
    secret?: string; //generated when omitted
};
export type ApiWebhookUpdateReq = {
    enabled?: boolean;
    events?: string[];
};
export type ApiWebhookCreateResp = ApiResp<{
    webhook: ApiWebhookRecord;
    secret: string; //shown once
}>;
export type ApiWebhooksListResp = ApiResp<{
    webhooks: ApiWebhookRecord[];
    eventTypes: ApiEventType[];
}>;
export type ApiWebhookResp = ApiResp<{ webhook: ApiWebhookRecord }>;

export type ApiWebhookDeliveryStatus = 'pending' | 'ok' | 'failed';
export type ApiWebhookDelivery = {
    id: string;
    webhookId: string;
    eventId: string;
    eventType: ApiEventType;
    status: ApiWebhookDeliveryStatus;
    attempts: number;
    httpStatus: number | null;
    error: string | null;
    createdAt: number;
    lastAttemptAt: number | null;
    nextAttemptAt: number | null;
};
export type ApiWebhookDeliveriesResp = ApiResp<{ deliveries: ApiWebhookDelivery[] }>;
export type ApiWebhookTestResp = ApiResp<{ delivery: ApiWebhookDelivery }>;

/** Body of every webhook POST. Signature header: `t=<ts>,v1=<hex hmac-sha256 of "<ts>.<body>">`. */
export type ApiWebhookPayload = {
    event: ApiEvent;
    webhookId: string;
    deliveryId: string;
    attempt: number;
    server: { name: string; txAdminVersion: string };
};
